import React, { useState, useEffect, useCallback } from "react";

import { AppDispatch, RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFeatureGroupWithFeatureData,
  updateCurrentStep,
  updateFeatureStatus,
} from "../../store/slices/featureGroupWithFeatureSlice";
import {
  fetchApplicationDomainData,
  updateSelectedUsers,
} from "../../store/slices/applicationDomainSlice";
import ApplicationAccessHeadCard from "../ApplicationAccessHeadCard/ApplicationAccessHeadCard";
import CommonLoader from "../../Components/CommonLoader/CommonLoader";
import FeaturePermissionsNew from "../FeaturePermissionsNew/FeaturePermissionsNew";
import { BrowserStorage } from "../../utils/browserStorage";
import { sessionKey } from "../../Constants/appConstants";
import SelectUsers from "./SelectUsers";
import { setClonedFeatureIds } from "../../store/slices/featurePermissionSlice";
import FeatureGroups from "./FeatureGroups";

interface Feature {
  disabled: any;
  id: number;
  featureName: string;
  featureDescription: string | null;
  isChecked?: boolean;
}

interface FeatureGroup {
  groupId: number;
  groupName: string;
  groupDescription: string;
  features: Feature[];
}

interface SelectedFeaturesState {
  [key: number]: Set<number>;
}

const ApplicationNew: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [data, setData] = useState<FeatureGroup[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchedFeatures, setSearchedFeatures] = useState<FeatureGroup[]>([]);
  const [applicationDomain, setApplicationDomain] = useState<any>([]);
  const [selectedUserList, setSelectedUserList] = useState<any>([]);
  const [selectedDomain, setSelectedDomain] = useState<any>({
    app_id: [],
    value_stream_id: [],
  });

  const {
    data: featureGroups,
    status,
    currentStep,
  } = useSelector((state: RootState) => state.featureGroupWithFeature);
  const {
    data: applicationDomainData,
    status: applicationDomainStatus,
    // selectedDomains,
  } = useSelector((state: RootState) => state.applicationDomain);
  // const [domainApiStatus, setDomainApiStatus] = useState<string>("idle");
  const [isAddingUser, setIsAddingUser] = useState<boolean>(false);
  // const [selectedDomain, setSelectedDomain] = useState<any>([
  //   { label: "ALL", id: "all" },
  // ]);

  const [isAdminOrSuperAdmin, setisAdminOrSuperAdmin] =
    useState<boolean>(false);

  const initialSelectedUser = async () => {
    try {
      const authObject = BrowserStorage.getSessionItem(sessionKey) ?? "";
      const authJson = JSON.parse(authObject);
      const { account } = authJson || {};
      const { idTokenClaims } = account || {};
      let currentUser;
      if (idTokenClaims?.name) {
        currentUser = {
          displayName: idTokenClaims?.name,
          mail: account?.username,
          user_workday_id: idTokenClaims?.WorkdayID,
        };
      }
      const roles = idTokenClaims?.roles || [];
      const hasAdminRole = roles?.some((role: string) =>
        ["RoleAdmin", "AppAdmin", "AppRoleApprover"].some((r) =>
          role.includes(r),
        ),
      );

      setisAdminOrSuperAdmin(hasAdminRole);
      return currentUser;
    } catch (error) {
      setisAdminOrSuperAdmin(false);
      return null;
    }
  };

  useEffect(() => {
    dispatch(updateCurrentStep(2));
    // Reset status to idle when component mounts to force fresh data fetch
    dispatch(updateFeatureStatus("idle"));
  }, []);

  useEffect(() => {
    if (currentStep === 1) {
      // const authObject = BrowserStorage.getSessionItem(sessionKey) ?? "";
      // const authJson = authObject ? JSON.parse(authObject) : null;
      // setSelectedUserList(
      //   selectedUserList.filter(
      //     (item: any) => item?.mail !== authJson?.account?.username,
      //   ),
      // );
      // setDomainApiStatus("idle");
      dispatch(setClonedFeatureIds([]));
    }
  }, [currentStep]);

  const getFeatureGroups = useCallback(async () => {
    const currentUser =
      selectedUserList?.length === 0 ? await initialSelectedUser() : null;
    dispatch(
      fetchFeatureGroupWithFeatureData({
        ...selectedDomain,
        userData:
          selectedUserList?.length > 0
            ? selectedUserList?.map((item: any) => ({
                displayName: item?.displayName,
                mail: item?.mail,
                user_workday_id: item?.user_workday_id,
              }))
            : currentUser
              ? [currentUser]
              : [],
      }),
    );
  }, [dispatch, selectedDomain, selectedUserList]);

  // Update local data state when featureGroups from Redux changes
  useEffect(() => {
    if (status === "succeeded" && featureGroups) {
      try {
        setData(featureGroups);

        const initialSelection: SelectedFeaturesState = {};
        featureGroups.forEach((group: any) => {
          initialSelection[group.groupId] = new Set();
        });
      } catch (error) {}
    }
  }, [status, featureGroups]);

  useEffect(() => {
    if (currentStep === 2) {
      if (applicationDomainStatus === "idle") {
        dispatch(fetchApplicationDomainData());
      }

      if (applicationDomainStatus === "succeeded" && applicationDomainData) {
        try {
          if (
            applicationDomainData?.length > 0 &&
            applicationDomainData !== undefined
          ) {
            // setSelectedDomain(applicationDomainData);
            setApplicationDomain(applicationDomainData);
            getFeatureGroups();
          }
        } catch (error) {}
      }
    }
  }, [
    dispatch,
    applicationDomainStatus,
    applicationDomainData,
    currentStep,
    selectedUserList,
    getFeatureGroups,
  ]);

  const handleSearchValue = useCallback(
    (query: string) => {
      const trimmedQuery = query; // No `.trim()` to allow spaces
      setSearchValue(trimmedQuery);

      if (trimmedQuery === "") {
        setSearchedFeatures([]); // Reset search when empty
        return;
      }

      const filteredGroups = data
        ?.map((group) => {
          const matchesGroupName = group?.groupName
            ?.toLowerCase()
            ?.includes(trimmedQuery?.toLowerCase());

          const filteredFeatures = group?.features?.filter((feature) =>
            feature?.featureName
              ?.toLowerCase()
              ?.includes(trimmedQuery?.toLowerCase()),
          );

          if (matchesGroupName || filteredFeatures?.length > 0) {
            return {
              ...group,
              features: matchesGroupName ? group.features : filteredFeatures,
            };
          }
          return null;
        })
        ?.filter(Boolean) as FeatureGroup[];

      setSearchedFeatures(filteredGroups);
    },
    [data],
  );

  if (currentStep === 1 && status === "loading") {
    return (
      <div className="p-6">
        <CommonLoader active={status === "loading"} />
      </div>
    );
  }

  return (
    <ApplicationAccessHeadCard
      getSearchValue={(query) => handleSearchValue(query)}
      setSearchValue={setSearchValue}
      searchValue={searchValue}
      currentStep={currentStep}
      isSearch={currentStep === 2}
      applicationDomain={applicationDomain}
      selectedDomain={selectedDomain}
      setSelectedDomain={setSelectedDomain}
      isAdminOrSuperAdmin={isAdminOrSuperAdmin}
      isAddingUser={isAddingUser}
      setIsAddingUser={setIsAddingUser}
    >
      {currentStep === 1 ? (
        <SelectUsers
          selectedUserList={selectedUserList}
          initialSelectedUser={initialSelectedUser}
          setIsAddingUser={setIsAddingUser}
          onNext={(selectedUserRows: any[]) => {
            setSelectedUserList(selectedUserRows);
            dispatch(updateSelectedUsers(selectedUserRows));
            dispatch(updateCurrentStep(2));
          }}
        />
      ) : currentStep === 2 ? (
        status === "loading" ? (
          <div className="p-6">
            <CommonLoader active={true} />
          </div>
        ) : (
          <FeatureGroups
            data={data}
            handleSearchValue={handleSearchValue}
            setSearchValue={setSearchValue}
            setSearchedFeatures={setSearchedFeatures}
            searchValue={searchValue}
            searchedFeatures={searchedFeatures}
            setData={setData}
            isAddingUser={isAddingUser}
          />
        )
      ) : (
        <FeaturePermissionsNew
          selectedUserList={selectedUserList}
          onBack={() => dispatch(updateCurrentStep(2))}
        />
      )}
    </ApplicationAccessHeadCard>
  );
};

export default ApplicationNew;
